import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Home from './pages/Home';

// Lazy load heavy components for code splitting
const Upload = lazy(() => import('./pages/Upload'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ThrottlingConfig = lazy(() => import('./pages/ThrottlingConfig'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const TestUpload = lazy(() => import('./pages/TestUpload'));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail'));
const TemplateManagement = lazy(() => import('./pages/TemplateManagement'));
const CardViewConfig = lazy(() => import('./pages/CardViewConfig'));
const OneTimeActivities = lazy(() => import('./pages/OneTimeActivities'));
const SearchFilterControls = lazy(() => import('./pages/SearchFilterControls'));
const DetectionTechniques = lazy(() => import('./pages/DetectionTechniques'));
const Logs = lazy(() => import('./pages/Logs'));
const Issues = lazy(() => import('./pages/Issues'));

// Loading component for lazy-loaded routes
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
    <div className="text-center animate-fade-in">
      <div className="relative mx-auto mb-4">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full animate-pulse-slow"></div>
        </div>
      </div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Redirect component for verification route
const VerificationRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/document/${id}?edit=true`} replace />;
};

function App() {
  return (
    <Router>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/throttling" element={<ThrottlingConfig />} />
                    <Route path="/templates" element={<TemplateManagement />} />
                    <Route path="/card-view-config" element={<CardViewConfig />} />
                    <Route path="/one-time-activities" element={<OneTimeActivities />} />
                    <Route path="/search-filter-controls" element={<SearchFilterControls />} />
                    <Route path="/detection-techniques" element={<DetectionTechniques />} />
                    <Route path="/logs" element={<Navigate to="/logs/audit" replace />} />
                    <Route path="/logs/:tab" element={<Logs />} />
                    <Route path="/audit-log" element={<Navigate to="/logs/audit" replace />} />
                    <Route path="/logging-config" element={<Navigate to="/logs/config" replace />} />
                    <Route path="/admin-logs" element={<Navigate to="/logs/recent" replace />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/test-upload" element={<TestUpload />} />
                    <Route path="/upload" element={<Upload />} />
                    <Route path="/issues" element={<Issues />} />
                    <Route path="/verification/:id" element={<VerificationRedirect />} />
                    <Route path="/document/:id" element={<DocumentDetail />} />
                    <Route path="/search" element={<Navigate to="/" replace />} />
                    <Route path="/deleted" element={<Home />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
